import axios from "axios";

const DUPLICATE_BLOCK_ENABLED = window.settings.blockDuplicateRequests ?? false;
const DUPLICATE_BLOCK_BY_TIME = window.settings.blockDuplicateRequestsByTime ?? false;
const DUPLICATE_REQUEST_WINDOW_MS = window.settings.duplicateRequestWindow ?? 200;

// Bilerek tekrar edebilecek endpoint'leri burada listele (substring match)
const EXCLUDED_PATHS = window.settings.duplicateBlockExcludePaths ?? [];

// Sadece bu method'larda duplicate kontrolü yap (settings'ten override edilebilir)
const BLOCKED_METHODS = (window.settings.duplicateBlockMethods ??
  ["get", "post", "put", "patch", "delete"]).map(m => m.toLowerCase());

// Eş zamanlı request'leri tutmak için
const pendingRequests = new Map();

// Zaman bazlı duplicate'leri tutmak için
const lastRequestTimestamps = new Map();

// İptal hatalarını caller'ların kolayca tanıyabilmesi için işaret
const DUPLICATE_MARKER = "__duplicateRequestBlocked__";

// Param/body içeriğini stabil ve FormData-uyumlu serialize et
function safeStringify(value) {
  if (value == null) return "";
  if (typeof FormData !== "undefined" && value instanceof FormData) {
    const parts = [];
    for (const [k, v] of value.entries()) {
      if (typeof File !== "undefined" && v instanceof File) {
        parts.push(`${k}=${v.name}:${v.size}:${v.lastModified}`);
      } else {
        parts.push(`${k}=${v}`);
      }
    }
    return parts.sort().join("&");
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return `Blob:${value.size}:${value.type}`;
  }
  if (typeof value === "string") return value;
  try {
    // Anahtarları sırala ki {a,b} ile {b,a} aynı key üretsin
    return JSON.stringify(value, Object.keys(value).sort());
  } catch {
    return "";
  }
}

function getRequestKey(config) {
  return [
    (config.method || "get").toLowerCase(),
    config.url,
    safeStringify(config.params),
    safeStringify(config.data)
  ].join("&");
}

function isExcluded(config) {
  const method = (config.method || "get").toLowerCase();
  if (!BLOCKED_METHODS.includes(method)) return true;
  const url = config.url || "";
  return EXCLUDED_PATHS.some(p => url.includes(p));
}



// --- Request interceptor ---
axios.interceptors.request.use(config => {
  if (!DUPLICATE_BLOCK_ENABLED) return config;
  if (isExcluded(config)) return config;

  const key = getRequestKey(config);
  const now = Date.now();

  // 1) Zaman penceresi kontrolü
  if (DUPLICATE_BLOCK_BY_TIME) {
    const lastTime = lastRequestTimestamps.get(key) || 0;
    if (now - lastTime < DUPLICATE_REQUEST_WINDOW_MS) {
      const controller = new AbortController();
      config.signal = controller.signal;
      const reason = { [DUPLICATE_MARKER]: true, reason: "time-window" };
      controller.abort(reason);
      return config;
    }
    lastRequestTimestamps.set(key, now);
    // Memory leak'i önlemek için pencere bitince temizle
    setTimeout(() => {
      const t = lastRequestTimestamps.get(key);
      if (t === now) lastRequestTimestamps.delete(key);
    }, DUPLICATE_REQUEST_WINDOW_MS * 2);
  }

  // 2) Eş zamanlı request kontrolü
  if (pendingRequests.has(key)) {
    const controller = new AbortController();
    config.signal = controller.signal;
    const reason = { [DUPLICATE_MARKER]: true, reason: "in-flight" };
    controller.abort(reason);
    return config;
  }

  pendingRequests.set(key, true);
  config.__requestKey = key;
  return config;
}, error => Promise.reject(error));

// --- Response interceptor ---
axios.interceptors.response.use(
  response => {
    if (response.config?.__requestKey) {
      pendingRequests.delete(response.config.__requestKey);
    }
    return response;
  },
  error => {
    if (error?.config?.__requestKey) {
      pendingRequests.delete(error.config.__requestKey);
    }
    return Promise.reject(error);
  }
);

export default axios;